<template>
  <div class="schedule-section mt-2">
    <div class="px-2">
      <p class="schedule-section-title">Schedule Details</p>
    </div>
    <div class="schedule-card">
      <div class="detail-list">
        <div class="detail-item">
          <div class="detail-icon">
            <v-icon size="16">mdi-pound</v-icon>
          </div>
          <div class="detail-copy">
            <p class="detail-label">Appointment No.</p>
            <p class="detail-value">{{ appointmentNo }}</p>
          </div>
        </div>
        <div class="detail-item">
          <div class="detail-icon">
            <v-icon size="16">mdi-clock-outline</v-icon>
          </div>
          <div class="detail-copy">
            <p class="detail-label">Date & Time</p>
            <p class="detail-value">{{ scheduleDateTime }}</p>
          </div>
        </div>
        <div class="detail-item">
          <div class="detail-icon">
            <v-icon size="16">mdi-map-marker-outline</v-icon>
          </div>
          <div class="detail-copy">
            <p class="detail-label">Service Address</p>
            <p class="detail-value">{{ fullAddress }}</p>
          </div>
        </div>
      </div>
      <div v-if="note" class="note-card">
        <div>
          <p class="note-label">Notes</p>
          <p class="note-text">{{ note }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, type PropType } from "vue";
import { formatTime, formatWeekdayMonthDay, getStatusColor } from "@/shared/utils/utils";

type AppointmentSchedule = {
  appointment_no?: string | null;
  status?: string | null;
  schedule_info?: {
    appointment_date: string | null;
    appointment_time: string | null;
    note: string | null;
  } | null;
  service_address?: {
    add1: string | null;
    add2: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
    country: string | null;
  } | null;
};

const props = defineProps({
  appointment: {
    type: Object as PropType<AppointmentSchedule>,
    required: true,
  },
});

const appointmentNo = computed(() => props.appointment.appointment_no || "-");
const scheduleDateTime = computed(() => {
  const s = props.appointment.schedule_info;
  if (!s?.appointment_date) return "-";
  const label = formatWeekdayMonthDay(s.appointment_date);
  return s.appointment_time ? `${label}, ${formatTime(s.appointment_time)}` : label;
});

const fullAddress = computed(() => {
  const a = props.appointment.service_address;
  if (!a) return "-";
  const parts = [a.add1, a.add2, a.city, a.state, a.zip, a.country]
    .map((p) => (p ?? "").trim())
    .filter(Boolean);
  return parts.length ? parts.join(", ") : "-";
});

const note = computed(() => props.appointment.schedule_info?.note || "");
</script>

<style scoped>
.schedule-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.schedule-section-title {
  margin: 0;
  font-size: 15px;
  font-weight: 800;
  color: #6b7280;
}

.schedule-card {
  overflow: hidden;
  border-radius: 18px;
  background: #fff;
  display: flex;
  flex-direction: column;
}

.detail-list {
  display: flex;
  flex-direction: column;
}

.detail-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-bottom: 1px solid #e5e7eb;
}

.detail-item:last-child {
  border-bottom: 0;
}

.detail-icon {
  width: 40px;
  height: 40px;
  border-radius: 14px;
  display: grid;
  place-items: center;
  background: rgba(249, 115, 22, 0.12);
  color: #f97316;
  flex-shrink: 0;
}

.detail-copy {
  min-width: 0;
}

.detail-label {
  margin: 0;
  font-size: 14px;
  color: var(--color-subtitle-light);
  font-weight: 500;
}

.detail-value {
  margin: 2px 0;
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}

.note-card {
  margin-top: 8px;
  border-radius: 12px;
  padding: 12px;
  display: flex;
  gap: 10px;
  align-items: flex-start;
}

.note-label {
  margin: 0;
  font-size: var(--font-size-default);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 800;
}

.note-text {
  font-size: var(--font-size-default);
  color: var(--color-title);
}
</style>
